// Anxiety Assessment Questions based on GAD-7 (Generalized Anxiety Disorder 7-item scale)
export const anxietyAssessmentQuestions = [
  {
    id: 1,
    category: "Nervousness & Worry",
    question: "Over the last 2 weeks, how often have you been bothered by feeling nervous, anxious, or on edge?",
    options: [
      { text: "Not at all", score: 0, explanation: "No anxiety symptoms" },
      { text: "Several days", score: 1, explanation: "Mild anxiety episodes" },
      { text: "More than half the days", score: 2, explanation: "Moderate anxiety frequency" },
      { text: "Nearly every day", score: 3, explanation: "High anxiety frequency" }
    ]
  },
  {
    id: 2,
    category: "Worry Control",
    question: "Over the last 2 weeks, how often have you been bothered by not being able to stop or control worrying?",
    options: [
      { text: "Not at all", score: 0, explanation: "Good worry control" },
      { text: "Several days", score: 1, explanation: "Occasional control issues" },
      { text: "More than half the days", score: 2, explanation: "Frequent control problems" },
      { text: "Nearly every day", score: 3, explanation: "Persistent worry control issues" }
    ]
  },
  {
    id: 3,
    category: "Excessive Worry",
    question: "Over the last 2 weeks, how often have you been bothered by worrying too much about different things?",
    options: [
      { text: "Not at all", score: 0, explanation: "No excessive worrying" },
      { text: "Several days", score: 1, explanation: "Some excessive worry" },
      { text: "More than half the days", score: 2, explanation: "Frequent excessive worry" },
      { text: "Nearly every day", score: 3, explanation: "Constant excessive worry" }
    ]
  },
  {
    id: 4,
    category: "Restlessness",
    question: "Over the last 2 weeks, how often have you been bothered by trouble relaxing?",
    options: [
      { text: "Not at all", score: 0, explanation: "Can relax easily" },
      { text: "Several days", score: 1, explanation: "Some difficulty relaxing" },
      { text: "More than half the days", score: 2, explanation: "Frequent relaxation problems" },
      { text: "Nearly every day", score: 3, explanation: "Persistent inability to relax" }
    ]
  },
  {
    id: 5,
    category: "Physical Restlessness",
    question: "Over the last 2 weeks, how often have you been bothered by being so restless that it's hard to sit still?",
    options: [
      { text: "Not at all", score: 0, explanation: "No restlessness" },
      { text: "Several days", score: 1, explanation: "Mild restlessness" },
      { text: "More than half the days", score: 2, explanation: "Significant restlessness" },
      { text: "Nearly every day", score: 3, explanation: "Severe restlessness" }
    ]
  },
  {
    id: 6,
    category: "Irritability",
    question: "Over the last 2 weeks, how often have you been bothered by becoming easily annoyed or irritable?",
    options: [
      { text: "Not at all", score: 0, explanation: "No irritability issues" },
      { text: "Several days", score: 1, explanation: "Occasional irritability" },
      { text: "More than half the days", score: 2, explanation: "Frequent irritability" },
      { text: "Nearly every day", score: 3, explanation: "Persistent irritability" }
    ]
  },
  {
    id: 7,
    category: "Fear & Apprehension",
    question: "Over the last 2 weeks, how often have you been bothered by feeling afraid as if something awful might happen?",
    options: [
      { text: "Not at all", score: 0, explanation: "No fearful thoughts" },
      { text: "Several days", score: 1, explanation: "Some fearful episodes" },
      { text: "More than half the days", score: 2, explanation: "Frequent fearful thoughts" },
      { text: "Nearly every day", score: 3, explanation: "Constant fear and apprehension" }
    ]
  }
];

export const anxietyLevels = {
  minimal: {
    range: [0, 4],
    level: "Minimal Anxiety",
    color: "from-green-400 to-green-600",
    icon: "😌",
    description: "You're experiencing minimal anxiety symptoms. This is within the normal range.",
    recommendations: [
      "Continue your current stress management practices",
      "Maintain regular exercise and healthy lifestyle habits", 
      "Practice mindfulness or relaxation techniques regularly",
      "Keep nurturing supportive relationships",
      "Monitor your mental health and be aware of changes"
    ],
    emergencyLevel: false
  },
  mild: {
    range: [5, 9],
    level: "Mild Anxiety",
    color: "from-yellow-400 to-yellow-600",
    icon: "😐",
    description: "You're experiencing mild anxiety that may benefit from attention and self-care strategies.",
    recommendations: [
      "Practice deep breathing exercises daily",
      "Try progressive muscle relaxation techniques",
      "Maintain regular sleep schedule and exercise routine",
      "Limit caffeine and alcohol consumption",
      "Consider mindfulness meditation or yoga",
      "Talk to friends, family, or a counselor about your feelings"
    ],
    emergencyLevel: false
  },
  moderate: {
    range: [10, 14],
    level: "Moderate Anxiety", 
    color: "from-orange-400 to-orange-600",
    icon: "😟",
    description: "You're experiencing moderate anxiety that may be impacting your daily functioning.",
    recommendations: [
      "Consider speaking with a mental health professional",
      "Practice anxiety management techniques daily",
      "Challenge negative thought patterns with cognitive techniques",
      "Establish a regular routine and structure",
      "Consider joining a support group",
      "Limit exposure to anxiety triggers when possible",
      "Focus on self-care and stress reduction"
    ],
    emergencyLevel: false
  },
  severe: {
    range: [15, 21],
    level: "Severe Anxiety",
    color: "from-red-400 to-red-600", 
    icon: "😰",
    description: "You're experiencing severe anxiety that likely requires professional support.",
    recommendations: [
      "Seek professional mental health treatment immediately",
      "Contact your doctor or a mental health provider",
      "Consider therapy (CBT is particularly effective for anxiety)",
      "Discuss medication options with a healthcare provider",
      "Reach out to trusted friends and family for support",
      "Use crisis resources if you feel overwhelmed",
      "Focus on basic self-care: sleep, nutrition, gentle exercise"
    ],
    emergencyLevel: true
  }
};

export const anxietyManagementTips = {
  immediate: [
    "Use the 4-7-8 breathing technique: Inhale for 4, hold for 7, exhale for 8",
    "Try the 5-4-3-2-1 grounding technique: 5 things you see, 4 you touch, 3 you hear, 2 you smell, 1 you taste",
    "Practice progressive muscle relaxation starting from your toes",
    "Take a cold shower or splash cold water on your face",
    "Go for a walk or do light exercise"
  ],
  daily: [
    "Maintain a consistent sleep schedule of 7-9 hours",
    "Limit caffeine intake, especially in the afternoon",
    "Practice mindfulness meditation for 10-15 minutes",
    "Exercise regularly - even light walking helps",
    "Keep a worry journal to track triggers and patterns",
    "Stay connected with supportive friends and family"
  ],
  longTerm: [
    "Learn cognitive behavioral techniques to challenge anxious thoughts",
    "Develop healthy coping mechanisms and stress management skills", 
    "Consider therapy - CBT, exposure therapy, or other evidence-based treatments",
    "Build a strong support network",
    "Practice self-compassion and acceptance",
    "Consider mindfulness-based stress reduction (MBSR) programs"
  ]
};